import React, { PropsWithChildren } from "react";

import { css, cx } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { patientFormattedName } from "@/models/UserModel.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { ColoredCard } from "@/uiKit/atoms/ColoredCard.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

export type PatientSuggestionCardProps = PropsWithChildren<{
  ref?: React.Ref<HTMLDivElement> | React.RefCallback<HTMLDivElement>;
  patientId: string;
  patientFirstName: string;
  patientLastName: string;
  subHeaderTag?: React.ReactNode;
  onValidate: () => void;
  onDecline: () => void;
  source: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}>;

export const PatientSuggestionCard: React.FC<PatientSuggestionCardProps> = ({
  ref,
  patientId,
  patientFirstName,
  patientLastName,
  subHeaderTag,
  onValidate,
  onDecline,
  source,
  actions,
  children,
  className,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  return (
    <ColoredCard
      ref={ref}
      color="yellow"
      id={`patient-suggestion-card-${patientId}`}
      className={cx(styles.container, className)}
    >
      <CardHeader
        title={
          subHeaderTag ? (
            <div className={styles.headerTitle}>
              <Typography variant="body" className={styles.headerText}>
                {patientFormattedName(patientLastName, patientFirstName)}
              </Typography>
              {subHeaderTag}
            </div>
          ) : (
            patientFormattedName(patientLastName, patientFirstName)
          )
        }
        action={source}
      />
      <CardContent>{children}</CardContent>
      <CardFooter className={styles.actions}>
        {actions || (
          <>
            <Button data-action="inclusion.suggestion.reject" variant="contained" onClick={onDecline}>
              {t("inclusionAgent.reject")}
            </Button>
            <Button data-action="inclusion.suggestion.accept" variant="contained" onClick={onValidate}>
              {t("inclusionAgent.accept")}
            </Button>
          </>
        )}
      </CardFooter>
    </ColoredCard>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({}),
  headerTitle: css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(2),
  }),
  headerText: css({
    fontWeight: "bold",
  }),
  actions: css({
    justifyContent: "flex-start",
    gap: theme.spacing(2),
  }),
});
